import { supabase } from '../supabase-client';
import { logError } from '../api/error-logger';

type TradingStrategy = {
  symbol: string;
  orderType: 'Market' | 'Limit';
  side: 'Buy' | 'Sell';
  quantity: number;
  price?: number;
  stopLoss?: number;
  takeProfit?: number;
  leverage: number;
  timeInForce: 'GoodTillCancel' | 'ImmediateOrCancel' | 'FillOrKill';
  reduceOnly: boolean;
  closeOnTrigger: boolean;
  trailingStop?: {
    enabled: boolean;
    type: 'Distance' | 'Rate';
    value: number;
    activationPrice?: number;
  };
};

type TradingRule = {
  condition: string;
  action: 'BUY' | 'SELL';
  timeframe?: string;
  parameters?: Record<string, any>;
};

// Parse Pine Script into structured format
export function parsePineScript(script: string): TradingRule[] {
  const rules: TradingRule[] = [];
  const lines = script.split('\n').filter(line => line.trim());
  let currentFunction = '';
  let parameters: Record<string, any> = {};

  for (const line of lines) {
    const trimmedLine = line.trim().toLowerCase();
    
    // Skip comments and empty lines
    if (trimmedLine.startsWith('//') || !trimmedLine) continue;

    // Parse strategy parameters
    if (trimmedLine.includes('input(')) {
      const match = line.match(/input\((.*?),(.*?)\)/);
      if (match) {
        const [_, value, name] = match;
        parameters[name.trim().replace(/['"]/g, '')] = parseFloat(value) || value;
      }
      continue;
    }

    // Parse entry conditions
    if (trimmedLine.includes('strategy.entry')) {
      const side = trimmedLine.includes('strategy.long') ? 'BUY' : 'SELL';
      let condition = '';

      // Look for the last if condition
      const lines = script.split('\n');
      for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].includes('if (')) {
          condition = lines[i].replace('if (', '').replace(')', '').trim();
          break;
        }
      }

      rules.push({
        condition,
        action: side,
        parameters: { ...parameters }
      });
    }

    // Parse exit conditions
    if (trimmedLine.includes('strategy.exit')) {
      const match = line.match(/strategy\.exit\((.*?)\)/);
      if (match) {
        const params = match[1].split(',').reduce((acc: any, param) => {
          const [key, value] = param.split('=').map(p => p.trim());
          if (key && value) acc[key] = value;
          return acc;
        }, {});

        rules.push({
          condition: 'Exit condition',
          action: 'SELL',
          parameters: {
            ...parameters,
            ...params
          }
        });
      }
    }
  }

  return rules;
}

// Add this function to combine user and admin settings
async function combineSettings(botId: string, userId: string, adminSettings: any) {
  try {
    // Get user's bot settings
    const { data: userBotStatus, error: statusError } = await supabase
      .from('user_bot_status')
      .select('settings')
      .eq('bot_id', botId)
      .eq('user_id', userId)
      .single();

    if (statusError && statusError.code !== 'PGRST116') {
      throw statusError;
    }

    // Get user's trading account settings
    const { data: userProfile, error: profileError } = await supabase
      .from('profiles')
      .select('trading_account')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;

    // Get selected trading pairs
    const { data: selectedPairs, error: pairsError } = await supabase
      .from('bot_trading_pairs')
      .select('pair_symbol')
      .eq('bot_id', botId);

    if (pairsError) throw pairsError;

    // Merge settings with priority:
    // 1. User's bot-specific settings (highest priority)
    // 2. User's trading account settings
    // 3. Admin's bot settings (base settings)
    const mergedSettings = {
      ...adminSettings,
      trading_pairs: selectedPairs?.map(p => p.pair_symbol) || adminSettings.trading_pairs,
      max_position_size: userBotStatus?.settings?.position_size || adminSettings.max_position_size,
      trading_account: userProfile?.trading_account || {},
      ...userBotStatus?.settings
    };

    return mergedSettings;
  } catch (error) {
    console.error('Error merging settings:', error);
    return adminSettings; // Return admin settings as fallback
  }
}

// Update the generateTradingStrategy function
export async function generateTradingStrategy(
  botId: string,
  rulesText: string,
  botSettings: any,
  userId?: string // Add optional userId parameter
): Promise<TradingStrategy | null> {
  try {
    // Parse rules first
    const rules = parsePineScript(rulesText);
    if (rules.length === 0) {
      throw new Error('No valid trading rules found');
    }

    // Combine settings if userId is provided
    const finalSettings = userId ? 
      await combineSettings(botId, userId, botSettings) : 
      botSettings;

    // Get current market price
    const { data: marketData } = await supabase
      .from('market_data')
      .select('price')
      .eq('symbol', finalSettings.trading_pairs[0])
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    const currentPrice = marketData?.price || 0;

    // Extract parameters from rules
    const params = rules.reduce((acc, rule) => ({
      ...acc,
      ...rule.parameters
    }), {});

    // Determine trading side based on rules
    const buySignals = rules.filter(r => r.action === 'BUY').length;
    const sellSignals = rules.filter(r => r.action === 'SELL').length;
    const tradingSide = buySignals > sellSignals ? 'Buy' : 'Sell';

    // Calculate position size based on user settings
    const positionSize = finalSettings.max_position_size;
    const quantity = positionSize / currentPrice;

    // Calculate stop loss and take profit
    const stopLoss = tradingSide === 'Buy'
      ? currentPrice * (1 - finalSettings.stop_loss_percentage / 100)
      : currentPrice * (1 + finalSettings.stop_loss_percentage / 100);

    const takeProfit = tradingSide === 'Buy'
      ? currentPrice * (1 + finalSettings.take_profit_percentage / 100)
      : currentPrice * (1 - finalSettings.take_profit_percentage / 100);

    // Create strategy object
    const strategy: TradingStrategy = {
      symbol: finalSettings.trading_pairs[0],
      orderType: 'Market',
      side: tradingSide,
      quantity,
      price: currentPrice,
      stopLoss,
      takeProfit,
      leverage: finalSettings.leverage,
      timeInForce: 'GoodTillCancel',
      reduceOnly: false,
      closeOnTrigger: false,
      trailingStop: finalSettings.trailing_stop
    };

    // Log strategy generation
    await logError({
      bot_id: botId,
      user_id: userId,
      error_type: 'STRATEGY',
      error_message: 'Strategy generated successfully',
      error_details: {
        strategy,
        settings: finalSettings
      }
    });

    return strategy;
  } catch (error: any) {
    await logError({
      bot_id: botId,
      user_id: userId,
      error_type: 'STRATEGY',
      error_message: 'Failed to generate trading strategy',
      error_details: error
    });
    throw error;
  }
}

export default {
  parsePineScript,
  generateTradingStrategy
};
